<template lang="pug">
Drawer(position="right" title="Manage Your Cookie Settings" :disableScroll="false" :preventClose="true")
  .cookie-container
    .intro-container
      p.intro {{ __('When you visit any website, it may store or retrieve information on your browser, mostly in the form of cookies. This information might be about you, your preferences or your device and is mostly used to make the site work as you expect it to.') }}
      br
      p.intro-secondary {{__('For more information, see our ')}}
        rogue-link.cookie-link(:to="policyLink") {{ __(policyText) }}
      Checkbox.input( type='checkbox' :disabled="true" :modelValue="true")
        .description-container
          .title {{ __('Strictly Necessary') }}
          .desc {{ __('These cookies perform various activities that are necessary to navigate the website and use its features.') }}
      Checkbox.input( type='checkbox' v-model="choices.performance")
        .description-container
          .title {{ __('Performance Cookies') }}
          .desc {{ __('These cookies tell us how you use the site and help us to improve it. This may include tracking site usage, visitor traffic, and user navigation patterns to ensure that users find what they’re looking for, and maintain fast load times.') }}
      Checkbox.input( type='checkbox' v-model="choices.functional" )
        .description-container
          .title {{ __('Functional Cookies') }}
          .desc {{ __('These cookies provide improved functionality and personalization based on your navigation and search. They may be activated by Rogue or by a third party provider, whose services have been added to our pages.') }}
      Checkbox.input( type='checkbox' v-model="choices.marketing" :disabled="gpcValue" )
        .description-container
          .title {{ __('Sales, Sharing, and Targeting Cookies') }}
          .desc {{ __('These cookies may be set within our website by our advertising partners. They may be used by these companies to build a profile of your interests and provide you with relevant advertisements on other websites. They do not directly store personal data, but are based on the unique identification of your browser and Internet device. If you do not allow these cookies, your advertising will be less targeted.') }}
    .button-container
      RogueButton(type="button" color="black" @click="confirm") {{__('Confirm My Choices')}}
      RogueButton(type="button" color="gray" @click="confirmAll") {{__('Accept All')}}
  </template>

<script lang="ts" setup>
import { onMounted, reactive } from 'vue'
import { useCookieManagement } from './useCookieManagement'
import { useRogue } from '@/js/services/Rogue'
import { getCookie, setCookie } from '@/js/services/Utils'
import { useUIStore } from '@/js/stores/uiStore'
import { RudderstackConsumer } from '@/js/services/analytics/consumers/rs'

const emit = defineEmits(['close'])

const rogue = useRogue()
const uiStore = useUIStore()

const { policyText, policyLink, gpcValue, removeCookie, requiresCookieOptIn, COOKIE_NAME, MARKETING_COOKIES} = useCookieManagement()

const choices = reactive({
  functional  : !requiresCookieOptIn.value,
  performance : !requiresCookieOptIn.value,
  marketing   : gpcValue ? false : !requiresCookieOptIn.value,
})

// If we have an existing value, it means we might need to REMOVE cookies after they save the form
let hasExistingValue = false
onMounted(() => {
  const cookie = getCookie(COOKIE_NAME)
  hasExistingValue = !!cookie

  if (cookie) {
    const cookieObject = JSON.parse(cookie)

    choices.functional = !!cookieObject.functional
    choices.performance = !!cookieObject.performance
    choices.marketing = !!cookieObject.marketing
  }
})

function confirm() {
  setCookie(COOKIE_NAME, JSON.stringify(choices))

  let toRemove: string[] = []
  
  if (!choices.functional) {
    toRemove = toRemove.concat(['rl_group_id', 'rl_group_trait', 'rl_anonymous_id', 'rl_page_init_referrer', 'rl_page_init_referring_domain', 'rl_session', 'rl_trait', 'rl_user_id'])
  }

  if (!choices.marketing || gpcValue) {
    toRemove = toRemove.concat(MARKETING_COOKIES)
  }
  // Performance cookies will be managed via gtag instead of deleted

  try {
    toRemove.forEach(name => removeCookie(name))
    rogue.log.debug('Removed cookies', toRemove)
  } catch (e) {
    rogue.log.warn('Error removing cookie', e as Error)
  }

  const rsConsumer = rogue.track.consumers.find(c => c.name === 'Rudderstack') as RudderstackConsumer

  // Populate the consent IDs based on the user's choice and send them to the RS consumer.
  rsConsumer.setPermissions(choices.marketing, choices.performance, choices.functional)
  

  if (hasExistingValue) {
    // Since this is a rare action, we can reload the window to remove the trackers
    window.location.reload()
  }

  if (!rogue.track.initialized) {
    // Initialize the tracking system now that cookiePreferences is set
    rogue.track.init(rogue)
  }

  emit('close')
  uiStore.hideOverlay()
}

function confirmAll() {
  choices.functional = true
  choices.marketing = !gpcValue
  choices.performance = true
  confirm()
}

</script>

<style lang="scss" scoped>
.cookie-container {
  min-height: 98%;
}

.intro-container {
  padding: 0 $space-xxs;
  margin-top: $space-xxs;
  margin-bottom: 0;

  .intro {
    margin-bottom: $space-xxxs;
    font-size: $size-xs;
  }

  .intro-secondary {
    font-size: 12px;
    line-height: 1.5em;
    margin-bottom: $size-l;
  }

  .cookie-link {
    margin: 0;
    padding: 0;
  }
}

label> :nth-child(2) {
  margin-top: $size-xxs;
  align-self: start;
}

.input {
  border-top: 1px solid #e9e9e9;
  margin: $space-xxxs 0 0 0;
  padding: 0 $space-xxs;

  .title {
    line-height: 1.5em;
    margin: $space-xxs 0;
    font-size: $size-m;
    font-weight: $weight-bold;
  }

  .desc {
    margin-bottom: $space-xxs;
    line-height: 1.5em;
  }

}

.button-container {
  margin-top: auto;
  display: flex;
  margin: 16px;
  gap: 15px;

  Button {
    flex: 1;
  }
}
</style>
<style lang="scss" scoped target="app">
  .platform-ios, .platform-android {
    button {
      margin-bottom: $app-gap-default;
    }
  }
</style>